import {FC, useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {getMyPartnerExpertProfile} from '../../services/partnerExpertService'
import {Expert, formatRate} from '../bookExpert/expertDirectory'

// WP-X8 — a partner views their OWN registered expert profile (self-detail). Reuses the X5 expert
// display helpers. Registration/edit is /partner/expert/register. Against #46's
// /expertise/my-partner-expert-profile/ — BLOCKED on #46 deploy (verification deferred).
const PartnerExpertProfilePage: FC = () => {
  const history = useHistory()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [profile, setProfile] = useState<Expert | null>(null)
  const [loading, setLoading] = useState(true)
  // 'none' = no profile yet (404); 'forbidden' = not a partner (403); 'error' = other/blocked.
  const [state, setState] = useState<'ok' | 'none' | 'forbidden' | 'error'>('ok')

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let active = true
    getMyPartnerExpertProfile()
      .then((data) => {
        if (!active) return
        setProfile(data)
        setState('ok')
      })
      .catch((err) => {
        if (!active) return
        const code = err?.response?.status
        setState(code === 404 ? 'none' : code === 403 ? 'forbidden' : 'error')
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  if (loading) return <div className='text-center py-10'>Loading…</div>

  if (state === 'forbidden')
    return <div className='text-center py-10 text-muted'>This area is for partner accounts.</div>

  if (state === 'error')
    return (
      <div className='text-center py-10 text-muted'>
        Your expert profile isn’t available right now — this is blocked on the #46 backend deploy.
      </div>
    )

  if (state === 'none' || !profile)
    return (
      <div className='partner-expert-page'>
        <div className='card p-8 text-center'>
          <h1 className='mb-2'>Become a listed expert</h1>
          <p className='text-muted mb-6'>
            Register your firm as an expert so members can find and engage you.
          </p>
          <div>
            <button
              className='btn nb-btn-primary'
              onClick={() => history.push('/partner/expert/register')}
            >
              Register as an expert
            </button>
          </div>
        </div>
      </div>
    )

  return (
    <div className='partner-expert-page'>
      <div className='card p-8'>
        <div className='d-flex flex-wrap justify-content-between align-items-start mb-6 gap-3'>
          <div>
            <h1 className='mb-1'>My expert profile</h1>
            <div className='d-flex gap-2 mt-2'>
              {profile.isListed ? (
                <span className='badge badge-light-success'>Listed</span>
              ) : (
                <span className='badge badge-light-warning'>Not listed</span>
              )}
              {profile.active === false && (
                <span className='badge badge-light-danger'>Inactive</span>
              )}
            </div>
          </div>
          <div className='d-flex align-items-center gap-4'>
            <span className='fs-4 fw-bold'>
              {formatRate(profile.listedRate, profile.currency, profile.rateType)}
            </span>
            <button
              className='btn btn-outline btn-outline-dark'
              onClick={() => history.push('/partner/expert/register')}
            >
              Edit profile
            </button>
          </div>
        </div>

        {profile.headline && <div className='fs-5 text-gray-800 mb-4'>{profile.headline}</div>}

        {(profile.expertiseAreas ?? []).length > 0 && (
          <div className='mb-4'>
            <div className='text-muted fs-8 text-uppercase mb-2'>Expertise</div>
            <div className='d-flex flex-wrap gap-2'>
              {(profile.expertiseAreas ?? []).map((a) => (
                <span key={a.id} className='badge badge-light-primary'>
                  {a.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {profile.availabilityNote && (
          <div className='mb-4'>
            <div className='text-muted fs-8 text-uppercase mb-1'>Availability</div>
            <div>{profile.availabilityNote}</div>
          </div>
        )}

        {profile.bio && (
          <div>
            <div className='text-muted fs-8 text-uppercase mb-1'>About</div>
            <div className='text-gray-700' style={{whiteSpace: 'pre-line'}}>
              {profile.bio}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PartnerExpertProfilePage
